/* SC cart personalized thumbnail — instant client thumb first-paint + print-host static swap.
 *
 * Contract (#62): the runtime stamps window.__pzThumbState = <stateId> at the confirmed add,
 * BEFORE the drawer sections render. This script (loaded on the PZ PDP and on cart surfaces):
 *  (1) INSTANT: for the stamped line, captures the live design canvas as a ~300px-wide webp data
 *      URL (2x of the 120-150px slot) into sessionStorage['pz_thumb_<stateId>'] and paints it
 *      immediately — real design, zero network. Later same-session renders reuse the stored copy.
 *  (2) STATIC SWAP: the durable print-host WebP thumb (the img's server-rendered src) is preloaded
 *      per stateId; only once it has confirmed-loaded is the current node swapped to it and the
 *      sessionStorage copy evicted. Cart re-renders may replace img nodes mid-flight, so all swap
 *      state is keyed by stateId — never by node. A failed preload keeps the instant thumb.
 *  (3) RESILIENCE (no instant available): the static src loads with a skeleton; on error, retry
 *      once after ~2s, then preserve the old personalized preview before the product photo.
 *
 * Operation-bound lines use __pzFrozenThumbs + pz:frozen-thumb, never the live canvas.
 * The exact product/variant/revision/generation tuple is checked again against the validated
 * __pzOperationBindings ACK before op -> state reuse. Legacy state stamps stay supported.
 * Read-only DOM presentation: never touches cart data, prices or the add flow.
 */
(function () {
  'use strict';
  if (window.__scCartThumbInit) return;
  window.__scCartThumbInit = true;
  var RETRY_MS = 2000;
  // R1 C3.3 (17/09/2026, contract R1-IDENTITY-V2 §6, spec §12.3): `durableImageUi` is stamped by the
  // composed runtime bootstrap (window.__pzDurableImageUi). ON: (a) a locally painted instant thumb
  // is kept for the whole session — never swapped to the static image, never evicted (owner: the
  // image the shopper saw must not change); (b) a line WITHOUT a local thumb whose static thumb is
  // still pending (404) is polled 1/2/4/8 s, 20 s wall, ONE flight per state per tab, stopped when
  // hidden/removed/deadline; (c) the `?r=1` retry is skipped (a query is refused by delivery).
  // OFF (default): every branch below behaves exactly as before this delta.
  var durableUi = function () { try { return window.__pzDurableImageUi === true; } catch (e) { return false; } };
  var POLL_GAPS_MS = [1000, 2000, 4000, 8000];
  var POLL_WALL_MS = 20000;
  var pollState = {};      // stateId -> 'polling' | 'ready' | 'stopped'
  var instantPainted = {}; // stateId -> true once a local thumb was painted this session
  // Per-stateId static preload state: 'pending' | 'failed', plus the confirmed-loaded src.
  // Keyed by stateId so a drawer re-render that replaces the img node mid-preload still
  // resolves onto the live node, and later nodes mount straight onto the confirmed src.
  var staticState = {};
  var staticDoneSrc = {};

  function readInstant(stateId) {
    if (!stateId) return null;
    try { return window.sessionStorage.getItem('pz_thumb_' + stateId); } catch (e) { return null; }
  }

  function evictInstant(stateId) {
    var entry = operationEntries && operationEntries[stateId];
    try { window.sessionStorage.removeItem(entry ? OP_CACHE + entry.record.operationId : 'pz_thumb_' + stateId); } catch (e) { /* best-effort */ }
  }

  // Capture the live PDP design canvas downscaled to ~300px WIDE (2x of the 120-150px cart slot,
  // matching retina sharpness without multi-MB data URLs; portrait ~1:2 canvas => ~300x600).
  // Browsers without webp encode fall back to png, still downscaled. Returns null if the canvas
  // is absent, empty, tainted (CORS), or unencodable.
  function captureCanvasThumb() {
    // Modal-first: after an Edit Save the OPEN edit modal's canvas is the just-saved design,
    // while the PDP mount (if any) still draws the pre-edit one.
    var src = document.querySelector('.pz-cart-edit-modal.is-open [data-pz-canvas] canvas')
      || document.querySelector('[data-pz-canvas] canvas');
    if (!src || !src.toDataURL) return null;
    return encodeThumb(src);
  }

  // Same legacy encode profile for a decoded FROZEN blob; never consult the live canvas here.
  function encodeThumb(src) {
    if (!src || !(src.width > 0) || !(src.height > 0)) return null;
    var scale = Math.min(1, 300 / src.width);
    var off = document.createElement('canvas');
    off.width = Math.max(1, Math.round(src.width * scale));
    off.height = Math.max(1, Math.round(src.height * scale));
    var g = off.getContext && off.getContext('2d');
    if (!g) return null;
    g.drawImage(src, 0, 0, off.width, off.height);
    var url = off.toDataURL('image/webp', 0.8);
    return (url && url.indexOf('data:image') === 0) ? url : null;
  }

  function clearSkeleton(img) {
    var host = img.closest('.sc-pz-thumb') || img;
    if (host.classList) host.classList.remove('sc-pz-thumb--loading');
  }

  // Decoded-node registry: every re-render path (companion add/remove, vendor qty update,
  // section loads) replaces line markup and re-decodes the preview (white blink). Keep the
  // decoded <img> per state id and graft it into fresh markup — same pixels, no decode.
  var liveNodes = {};
  var decodedNodes = {}; // both drawer and /cart can be connected at once
  function registerLive(img, stateId) {
    if (!stateId || !img || !img.complete || !(img.naturalWidth > 0)) return;
    var nodes = decodedNodes[stateId] || (decodedNodes[stateId] = []);
    if (nodes.indexOf(img) === -1) nodes.push(img);
    liveNodes[stateId] = img;
  }
  function graftKept(img, stateId) {
    var kept = stateId ? liveNodes[stateId] : null;
    var nodes = decodedNodes[stateId] || [];
    for (var n = 0; n < nodes.length; n++) {
      if (nodes[n] !== img && !nodes[n].isConnected && nodes[n].complete && nodes[n].naturalWidth > 0) { kept = nodes[n]; break; }
    }
    if (!kept || kept === img || kept.isConnected) return false;
    if (!(kept.complete && kept.naturalWidth > 0)) return false;
    if (!img.parentNode) return false;
    kept.className = img.className;
    kept.classList.remove('sc-pz-thumb--loading');
    // An op -> state (or reverse) section must not inherit identity attributes it no longer has.
    var previous = Array.prototype.slice.call(kept.attributes);
    for (var p = 0; p < previous.length; p++) {
      var name = previous[p].name;
      if (name.indexOf('data-pz-') === 0 && name !== 'data-pz-thumb-local' && !img.hasAttribute(name)) kept.removeAttribute(name);
    }
    for (var i = 0; i < img.attributes.length; i++) {
      var a = img.attributes[i];
      if (a.name === 'src' || a.name === 'class') continue;
      kept.setAttribute(a.name, a.value);
    }
    kept.setAttribute('data-pz-thumb-init', '1');
    img.parentNode.replaceChild(kept, img);
    liveNodes[stateId] = kept;
    clearSkeleton(kept);
    return true;
  }

  function currentNodes(stateId) {
    var entry = operationEntries && operationEntries[stateId];
    if (entry) {
      if (operations[entry.record.operationId] !== entry) return [];
      var binding = bindingFor(entry), nodes = document.querySelectorAll(OP_SELECTOR), out = [];
      for (var i = 0; i < nodes.length; i++) {
        if (binding && matchesLine(nodes[i], entry, binding)) out.push(nodes[i]);
      }
      return out;
    }
    return document.querySelectorAll('img[data-pz-state-id="' + stateId + '"]');
  }

  // Preload the durable static preview once per stateId; on confirmed load, swap every CURRENT
  // node for that state to it and evict the sessionStorage copy (the static is now the source).
  // On failure the instant thumb stays — the per-node error path never ran because the node was
  // showing the data URL, so nothing regresses to a broken image.
  // Mount the confirmed-loaded durable static src on every current node for the state.
  function mountStatic(stateId, staticSrc) {
    staticState[stateId] = 'done';
    staticDoneSrc[stateId] = staticSrc;
    var imgs = currentNodes(stateId);
    for (var i = 0; i < imgs.length; i++) { imgs[i].src = staticSrc; imgs[i].removeAttribute('data-pz-thumb-fallback'); clearSkeleton(imgs[i]); }
  }

  function preloadStatic(stateId, staticSrc) {
    if (!stateId || !staticSrc || typeof Image !== 'function') return;
    // R1: a local thumb already shown stays for the session — no static swap, no eviction.
    if (durableUi() && instantPainted[stateId]) return;
    if (staticState[stateId] === 'pending' || staticDoneSrc[stateId]) return;
    staticState[stateId] = 'pending';
    var pre = new Image();
    pre.onload = function () {
      var swap = function () {
        if (durableUi() && instantPainted[stateId]) return;
        mountStatic(stateId, staticSrc);
        evictInstant(stateId);
      };
      // Paint-atomic: swap only once the static thumb is DECODED — a loaded-but-undecoded
      // src assignment blinks for a frame while the browser rasterizes (the "style race"
      // feel on the drawer right after an add — owner 2026-08-19).
      if (typeof pre.decode === 'function') { pre.decode().then(swap, swap); } else { swap(); }
    };
    pre.onerror = function () { staticState[stateId] = 'failed'; };
    pre.src = staticSrc;
  }

  // R1: bounded poll for a pending server thumb — only when NO local thumb exists for the state.
  // Uses an Image preload (no CORS/HEAD needed); success = confirmed decode, then every current
  // node for the state mounts the static src. Any 4xx/5xx just waits for the next gap.
  function pollPending(stateId, staticSrc, anyMode) {
    if ((!durableUi() && !anyMode) || !stateId || !staticSrc || typeof Image !== 'function') return;
    if (instantPainted[stateId] || staticDoneSrc[stateId] || pollState[stateId]) return;
    pollState[stateId] = 'polling';
    var startedAt = Date.now();
    var attempt = 0;
    var stop = function (why) { pollState[stateId] = why || 'stopped'; };
    var tick = function () {
      if (pollState[stateId] !== 'polling') return;
      if (document.hidden) { stop('stopped'); return; }
      if (!currentNodes(stateId).length) { stop('stopped'); return; }
      if (Date.now() - startedAt > POLL_WALL_MS) { stop('stopped'); return; }
      var pre = new Image();
      pre.onload = function () {
        var apply = function () {
          if (pollState[stateId] !== 'polling' || (durableUi() && instantPainted[stateId])) return;
          mountStatic(stateId, staticSrc);
          stop('ready');
        };
        if (typeof pre.decode === 'function') { pre.decode().then(apply, apply); } else { apply(); }
      };
      pre.onerror = function () {
        if (attempt >= POLL_GAPS_MS.length) { stop('stopped'); return; }
        var gap = POLL_GAPS_MS[attempt++];
        if (Date.now() - startedAt + gap > POLL_WALL_MS) { stop('stopped'); return; }
        setTimeout(tick, gap);
      };
      pre.src = staticSrc;
    };
    setTimeout(tick, 0);
  }

  // Operation thumbs are local presentation only. The producer owns capture + ACK validation.
  // A state alias is never inferred from variant, current canvas, or an event's detail payload.
  var operations = Object.create(null);
  var operationEntries = Object.create(null);
  var OP_SELECTOR = 'img[data-pz-thumb-primary="1"]';
  var OP_CACHE = 'pz_thumb_op_';
  var OP_CACHE_LIMIT = 8;
  var OP_CACHE_BYTES = 2 * 1024 * 1024;
  function own(registry, key) {
    return registry && Object.prototype.hasOwnProperty.call(registry, key) ? registry[key] : null;
  }
  function tuple(record) {
    if (!record || !/^[A-Za-z0-9._~-]{1,128}$/.test(record.operationId || '')
      || !/^\d+$/.test(String(record.productId || '')) || !/^\d+$/.test(String(record.variantId || ''))
      || !(Number.isInteger(record.revision) && record.revision > 0)
      || (typeof record.generationToken !== 'string' && typeof record.generationToken !== 'number')
      || String(record.generationToken) === '' || (typeof record.generationToken === 'number' && !Number.isFinite(record.generationToken))) return '';
    return JSON.stringify([record.operationId, String(record.productId), String(record.variantId), record.revision, record.generationToken]);
  }
  function bindingFor(entry) {
    var b = own(window.__pzOperationBindings, entry.record.operationId);
    if (!b || tuple(b) !== entry.tuple || !/^[a-z2-7]{26}$/.test(b.stateId || '')
      || (b.protocol !== 'reserve-v2' && b.protocol !== 'v1')
      || !/^[a-f0-9]{64}$/.test(b.contentSha256 || '') || !/^rel_[a-f0-9]{8,64}$/.test(b.releaseId || '')
      || !Array.isArray(b.answers)) return null;
    return b;
  }
  function answersMatch(props, answers) {
    var used = Object.create(null), compared = 0;
    for (var i = 0; i < answers.length; i++) {
      if (!answers[i] || typeof answers[i] !== 'object') return false;
      var base = String(answers[i].label == null ? '' : answers[i].label).trim().slice(0, 100);
      if (!base || base.charAt(0) === '_') base = 'Personalization';
      var name = base, n = 2;
      while (used[name]) { var suffix = ' #' + n++; name = base.slice(0, 100 - suffix.length) + suffix; }
      used[name] = true;
      if (!Object.prototype.hasOwnProperty.call(props, name)) return false;
      compared++;
      if (String(props[name]) !== String(answers[i].value == null ? '' : answers[i].value)) return false;
    }
    return !answers.length || compared > 0;
  }
  // P1 10/10: Liquid `item.properties | json` on the live storefront is not always a plain object
  // (observed: an array of name/value pairs). Normalize to {name: value}; anything else is refused.
  // Liquid HTML-escapes property names/values in this JSON (observed "Choose Gnome&#39;s Color");
  // decode the standard entities so they compare equal to the runtime's raw answer labels/values.
  function unescapeHtml(v) {
    if (typeof v !== 'string' || v.indexOf('&') === -1) return v;
    return v.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos);/gi, function (m, e) {
      var x = e.toLowerCase();
      if (x === 'amp') return '&'; if (x === 'lt') return '<'; if (x === 'gt') return '>';
      if (x === 'quot') return '"'; if (x === 'apos') return "'";
      var n = x.charAt(1) === 'x' ? parseInt(x.slice(2), 16) : parseInt(x.slice(1), 10);
      return n > 0 && n < 0x110000 ? String.fromCodePoint(n) : m;
    });
  }
  function lineProps(img) {
    var parsed = lineRaw(img);
    if (!parsed) return null;
    var out = {};
    for (var k in parsed) {
      if (!Object.prototype.hasOwnProperty.call(parsed, k)) continue;
      var key = unescapeHtml(k);
      if (Object.prototype.hasOwnProperty.call(out, key)) return null;
      out[key] = unescapeHtml(parsed[k]);
    }
    return out;
  }
  function lineRaw(img) {
    var raw;
    try { raw = JSON.parse(img.getAttribute('data-pz-thumb-properties') || 'null'); } catch (e) { return null; }
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw;
    if (!Array.isArray(raw)) return null;
    var out = {};
    for (var i = 0; i < raw.length; i++) {
      var e = raw[i], k, v;
      if (Array.isArray(e) && e.length === 2) { k = e[0]; v = e[1]; }
      else if (e && typeof e === 'object') { k = e.key != null ? e.key : (e.name != null ? e.name : e.first); v = e.value != null ? e.value : e.last; }
      if (typeof k !== 'string' || Object.prototype.hasOwnProperty.call(out, k)) return null;
      out[k] = v == null ? '' : v;
    }
    return out;
  }
  function matchesLine(img, entry, binding) {
    var r = entry.record, state = img.getAttribute('data-pz-state-id') || '';
    if (own(window.__pzFrozenThumbs, r.operationId) !== r || tuple(r) !== entry.tuple
      || !img.isConnected || img.getAttribute('data-pz-thumb-primary') !== '1'
      || img.getAttribute('data-pz-product-id') !== String(r.productId)
      || img.getAttribute('data-pz-variant-id') !== String(r.variantId)
      || !img.getAttribute('data-pz-line-key')) return false;
    var props = lineProps(img);
    if (!props || props._pz_parent_operation_id || props._pz_parent_state_id) return false;
    if (state) return !!binding && state === binding.stateId && answersMatch(props, binding.answers)
      && (!props._pz_operation_id || props._pz_operation_id === r.operationId);
    // Current op-only payload has revision 1. Edited lines must obtain their own state/operation.
    if (r.revision !== 1 || props._pz_operation_id !== r.operationId
      || props._pz_recovery_operation !== r.operationId || props._pz_needs_recovery !== '1') return false;
    if (binding && !answersMatch(props, binding.answers)) return false;
    if (!binding && entry.lineProperties && entry.lineProperties !== JSON.stringify(props)) return false;
    // Before ACK, a rotated key is not proof of the same frozen design.
    return !entry.lineKey || entry.lineKey === img.getAttribute('data-pz-line-key') || !!binding;
  }
  function saveOperation(entry) {
    // Bounded best effort; no answers, blobs, capabilities or localStorage. Memory still works
    // when storage is blocked/full. Persisted pixels require a current producer tuple to reuse.
    try {
      var serialized = JSON.stringify({ tuple: entry.tuple, url: entry.url });
      var store = window.sessionStorage, keys = [], total = serialized.length * 2;
      if (total > OP_CACHE_BYTES) return;
      for (var i = 0; i < store.length; i++) {
        var key = store.key(i);
        if (key && key.indexOf(OP_CACHE) === 0) keys.push(key);
      }
      for (var j = keys.length - 1; j >= 0; j--) {
        var value = store.getItem(keys[j]) || '';
        if (keys[j] === OP_CACHE + entry.record.operationId || j >= OP_CACHE_LIMIT - 1 || total + value.length * 2 > OP_CACHE_BYTES) store.removeItem(keys[j]);
        else total += value.length * 2;
      }
      store.setItem(OP_CACHE + entry.record.operationId, serialized);
    } catch (e) { /* quota/private mode */ }
  }
  function frozenEntry(op) {
    var record = own(window.__pzFrozenThumbs, op), signature = tuple(record);
    if (!signature || record.operationId !== op || !record.blob) return null;
    var entry = operations[op];
    if (entry && entry.tuple === signature && entry.blob === record.blob) return entry;
    entry = operations[op] = { record: record, tuple: signature, blob: record.blob, key: 'op:' + op + ':' + signature, url: '', lineKey: '' };
    operationEntries[entry.key] = entry;
    try {
      var saved = JSON.parse(window.sessionStorage.getItem(OP_CACHE + op) || 'null');
      if (saved && saved.tuple === signature && typeof saved.url === 'string' && saved.url.indexOf('data:image') === 0) entry.url = saved.url;
    } catch (e) { /* optional cache */ }
    if (!entry.url) decodeFrozenEntry(entry);
    return entry;
  }
  function decodeFrozenEntry(entry) {
    // A corrupt persisted URL may retry once from its immutable Blob. Never recapture
    // the live canvas or loop on an unencodable Blob/encoder result.
    if (entry.decodeAttempted) return;
    entry.decodeAttempted = true;
    var record = entry.record, op = record.operationId, signature = entry.tuple, objectUrl;
    try {
      objectUrl = URL.createObjectURL(record.blob);
      var source = new Image();
      function release() { URL.revokeObjectURL(objectUrl); }
      source.onerror = release;
      source.onload = function () {
        try {
          if (operations[op] !== entry || own(window.__pzFrozenThumbs, op) !== record || tuple(record) !== signature) return;
          entry.url = encodeThumb(source) || '';
          if (entry.url) saveOperation(entry);
        } catch (e) { /* tainted/unencodable: keep pending/product fallback */ }
        finally { release(); }
        scan(document);
      };
      source.src = objectUrl;
    } catch (e) { if (objectUrl) URL.revokeObjectURL(objectUrl); }
  }
  function clearOperation(img) {
    var entry = operations[img.getAttribute('data-pz-thumb-local')];
    if (!img.getAttribute('data-pz-thumb-local')) return;
    if (entry) {
      var nodes = decodedNodes[entry.key] || [], index = nodes.indexOf(img);
      if (index !== -1) nodes.splice(index, 1);
      if (liveNodes[entry.key] === img) delete liveNodes[entry.key];
    }
    img.removeAttribute('data-pz-thumb-local');
    var fallback = img.getAttribute('data-pz-product-fallback');
    if (fallback) img.src = fallback;
    img.setAttribute('data-pz-thumb-pending', '1');
  }
  // P1 10/10 (cart thumb broken after wave2): when the operation-bound local thumb cannot paint
  // (no frozen blob/ACK match yet, ambiguous keys, blob not decoded), hand the node back to the
  // pre-wave2 path (init: retry → old preview → product photo) instead of leaving the pending
  // _pz_thumb src (404 until the Worker PUT lands) with no error handling. A later
  // pz:frozen-thumb / pz:operation-bound scan still paints the local thumb through this function.
  function legacyPath(img) {
    clearOperation(img); // keeps the wave2 pending marker; only the fallback wiring is restored
    return false;
  }
  function operationThumb(img) {
    if (img.getAttribute('data-pz-thumb-primary') !== '1') { clearOperation(img); return false; }
    var op = img.getAttribute('data-pz-operation-id') || '', state = img.getAttribute('data-pz-state-id') || '';
    // A normal state line keeps its operation property after writeback. A state-only render may
    // reuse a local entry ONLY via the validated ACK, never a bare state cache alias.
    if (!op && state) {
      for (var id in operations) {
        var ack = bindingFor(operations[id]);
        if (ack && ack.stateId === state) { op = id; break; }
      }
    }
    if (!op) { clearOperation(img); return false; } // legacy state helper remains unchanged
    if (state && !own(window.__pzFrozenThumbs, op) && !operations[op]) return false;
    var entry = frozenEntry(op), binding = entry && bindingFor(entry);
    img.setAttribute('data-pz-thumb-pending', '1');
    img.setAttribute('aria-label', 'Personalized preview pending');
    if (!entry || !matchesLine(img, entry, binding)) return legacyPath(img);
    // Two distinct primary keys WITHIN a surface are ambiguous. A validated ACK can
    // rotate the drawer key before the still-connected /cart section is rerendered.
    var surface = img.closest('cart-drawer-items, cart-drawer, cart-items') || document;
    var nodes = (binding ? surface : document).querySelectorAll(OP_SELECTOR), keys = Object.create(null);
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].getAttribute('data-pz-operation-id') === op) keys[nodes[i].getAttribute('data-pz-line-key')] = true;
    }
    if (Object.keys(keys).length > 1) return legacyPath(img);
    entry.lineKey = img.getAttribute('data-pz-line-key');
    entry.lineProperties = JSON.stringify(lineProps(img));
    if (!entry.url) return legacyPath(img);
    var staticSrc = binding && state === binding.stateId ? img.getAttribute('data-pz-thumb-src') || img.getAttribute('data-pz-static-src') || '' : '';
    var paintedSrc = staticDoneSrc[entry.key] || entry.url;
    // Keep the operation identity across op -> state so the SAME decoded node/cache is reused.
    if (img.getAttribute('data-pz-thumb-local') !== op && graftKept(img, entry.key)) img = liveNodes[entry.key];
    if (img.getAttribute('src') !== paintedSrc) img.src = paintedSrc;
    img.setAttribute('data-pz-thumb-local', op);
    img.removeAttribute('data-pz-thumb-pending');
    if (img.getAttribute('aria-label') === 'Personalized preview pending') img.removeAttribute('aria-label');
    img.removeAttribute('data-pz-thumb-fallback');
    if (!img.__pzFrozenLoad) {
      img.__pzFrozenLoad = true;
      img.addEventListener('load', function () {
        // Read current identity after an alias/rerender, not a captured stale node binding.
        var current = operations[img.getAttribute('data-pz-thumb-local')];
        if (current && img.getAttribute('src') === current.url && matchesLine(img, current, bindingFor(current))) {
          clearSkeleton(img); registerLive(img, current.key);
        }
      });
      img.addEventListener('error', function () {
        var current = operations[img.getAttribute('data-pz-thumb-local')];
        if (!current || img.getAttribute('src') !== current.url) return;
        var failedUrl = current.url;
        current.url = '';
        evictInstant(current.key);
        delete liveNodes[current.key];
        delete decodedNodes[current.key];
        // Clear every surface before the second error callback observes url=''.
        var affected = document.querySelectorAll(OP_SELECTOR);
        for (var i = 0; i < affected.length; i++) {
          var node = affected[i];
          if (node.getAttribute('data-pz-thumb-local') !== current.record.operationId || node.getAttribute('src') !== failedUrl) continue;
          node.removeAttribute('data-pz-thumb-local');
          node.setAttribute('data-pz-thumb-pending', '1');
          node.setAttribute('aria-label', 'Personalized preview pending');
          var fallback = node.getAttribute('data-pz-product-fallback');
          if (fallback) node.src = fallback;
          clearSkeleton(node);
        }
        decodeFrozenEntry(current);
      });
    }
    if (img.complete && img.naturalWidth > 0) { clearSkeleton(img); registerLive(img, entry.key); }
    instantPainted[entry.key] = true;
    if (binding && state === binding.stateId) instantPainted[state] = true;
    if (staticSrc) preloadStatic(entry.key, staticSrc);
    return true;
  }

  function init(img) {
    if (!img) return;
    if (operationThumb(img)) return;
    if (img.getAttribute('data-pz-thumb-init') === '1') return;
    var graftState = img.getAttribute('data-pz-state-id') || '';
    if (graftKept(img, graftState)) return;
    img.setAttribute('data-pz-thumb-init', '1');
    var stateId = graftState;
    var previewFallback = img.getAttribute('data-pz-preview-fallback') || '';
    var productFallback = img.getAttribute('data-pz-product-fallback') || '';
    var staticSrc = img.getAttribute('data-pz-thumb-src') || img.getAttribute('src') || '';

    if (img.complete && img.naturalWidth > 0) { clearSkeleton(img); registerLive(img, stateId); }
    img.addEventListener('load', function () {
      if ((img.getAttribute('data-pz-state-id') || '') !== stateId) return;
      clearSkeleton(img); registerLive(img, stateId);
    }, { once: false });

    // Resilience is wired FIRST so it also covers a corrupt instant data URL: retry the static thumb
    // once, then preserve the old personalized preview before degrading to the product photo.
    var retried = false;
    var previewTried = false;
    var productTried = false;
    var onThumbError = function () {
      if ((img.getAttribute('data-pz-state-id') || '') !== stateId || img.getAttribute('data-pz-thumb-local')) return;
      // P1 10/10: never leave the broken image on screen while waiting to retry. The static thumb
      // is re-checked in the background (bounded preload, swapped in only once decoded) and the
      // node moves straight on to the old preview → product photo chain.
      if (!retried && staticSrc) {
        retried = true;
        if (stateId) pollPending(stateId, staticSrc, true);
      }
      if (!previewTried && previewFallback && img.getAttribute('src') !== previewFallback) {
        previewTried = true;
        img.setAttribute('data-pz-thumb-fallback', 'preview');
        img.src = previewFallback;
        return;
      }
      // Thumb and old personalized preview both failed: product photo is the terminal no-loop
      // fallback and a marker lets the backfill audit count the affected legacy cohort.
      if (!productTried && productFallback && img.getAttribute('src') !== productFallback) {
        productTried = true;
        img.src = productFallback;
      }
      img.setAttribute('data-pz-thumb-fallback', 'product-photo');
      // R1: the static thumb is pending (reserve-v2) — poll it, bounded, only without a local thumb.
      if (stateId && staticSrc) pollPending(stateId, staticSrc);
      // No console output (owner rule: the browser exposes nothing; the state id stays server-side).
      clearSkeleton(img);
    };
    img.addEventListener('error', onThumbError);
    // P1 10/10: the drawer can insert a line whose pending _pz_thumb already failed (404 before the
    // Worker PUT lands) before this script scans it; that error event is gone, so run the same
    // fallback chain now. A not-yet-requested lazy image is not complete and is left alone.
    if (img.complete && img.getAttribute('src') && !(img.naturalWidth > 0)) onThumbError();

    // Static already confirmed-loaded this session → mount straight onto the confirmed durable
    // src (a replacement node may have rendered with a different placeholder), no instant repaint.
    if (stateId && staticDoneSrc[stateId]) {
      if (img.getAttribute('src') !== staticDoneSrc[stateId]) img.src = staticDoneSrc[stateId];
      return;
    }
    // Static already complete on THIS node (e.g. cache hit before we ran) → it is the sharp final
    // image; painting a 300px instant over it and swapping back would only flicker.
    if (img.complete && img.naturalWidth > 0 && staticSrc && (img.currentSrc || img.src).indexOf(staticSrc) !== -1) {
      if (stateId) { staticState[stateId] = 'done'; staticDoneSrc[stateId] = staticSrc; evictInstant(stateId); }
      return;
    }

    // Instant client thumb — the runtime marked the just-added line's state id: capture the live
    // PDP design canvas for THAT line (and persist it for same-session re-renders); otherwise
    // reuse a stored capture. The copy is evicted only after the static preview confirmed-loads,
    // so a re-render racing the preload can still paint instantly.
    var instant = null;
    if (stateId && window.__pzThumbState === stateId) {
      try {
        instant = captureCanvasThumb();
        if (instant) { try { window.sessionStorage.setItem('pz_thumb_' + stateId, instant); } catch (e) { /* quota */ } }
      } catch (e) { /* best-effort */ }
      try { delete window.__pzThumbState; } catch (e) { /* best-effort */ }
    } else {
      instant = readInstant(stateId);
    }
    if (instant && instant.indexOf('data:image') === 0) {
      img.src = instant;
      if (stateId) instantPainted[stateId] = true;
      preloadStatic(stateId, staticSrc);
    }
  }

  function scan(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    var imgs = scope.querySelectorAll('img[data-pz-state-id]');
    for (var i = 0; i < imgs.length; i++) init(imgs[i]);
    var pending = scope.querySelectorAll('img[data-pz-operation-id]:not([data-pz-state-id])');
    for (var j = 0; j < pending.length; j++) init(pending[j]);
  }

  function boot() { scan(document); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
  // Cart drawer/section re-renders replace the line nodes; re-scan the new ones.
  document.addEventListener('shopify:section:load', function (e) { scan(e && e.target); });
  document.addEventListener('cart:refresh', function () { scan(document); });
  document.addEventListener('cart:updated', function () { scan(document); });
  document.addEventListener('pz:frozen-thumb', function () { scan(document); });
  document.addEventListener('pz:operation-bound', function () { scan(document); });
  if (typeof MutationObserver === 'function' && document.body) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (records[i].type === 'attributes') { init(records[i].target); continue; }
        var added = records[i].addedNodes || [];
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node && node.nodeType === 1) {
            if (node.matches && node.matches('img[data-pz-state-id], img[data-pz-operation-id]')) init(node);
            else scan(node);
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true, attributes: true,
      attributeFilter: ['data-pz-state-id', 'data-pz-operation-id', 'data-pz-thumb-primary', 'data-pz-product-id', 'data-pz-variant-id', 'data-pz-line-key', 'data-pz-thumb-properties'] });
  }
})();
